/**
 * Preorder Quickview Mobile
 * List-style variant selector for preorder products on mobile
 * On variant selection, immediately adds to cart
 */
(function() {
  'use strict';

  // DOM elements
  let preorderModal = null;
  let preorderOverlay = null;
  let optionsContainer = null;
  let loadingEl = null;
  let closeBtn = null;

  // Drag functionality variables
  let startY = 0;
  let currentY = 0;
  let isDragging = false;
  const dragThreshold = 100;

  /**
   * Initialize the preorder quickview modal
   */
  function init() {
    preorderModal = document.getElementById('preorderQuickviewMobile');
    preorderOverlay = document.querySelector('.preorder-overlay');
    optionsContainer = document.getElementById('preorderQuickviewOptions');
    loadingEl = document.getElementById('preorder-qv-loading');
    closeBtn = preorderModal?.querySelector('.preorder-quickview-mobile__close');

    if (!preorderModal) return;

    setupCloseHandlers();
    setupDragFunctionality();
    setupButtonClickHandlers();

    // Expose API globally
    window.preorderQuickviewMobile = {
      open: openWithProduct,
      close: closeModal
    };
  }

  /**
   * Open the modal
   */
  function openModal() {
    if (!preorderModal || !preorderOverlay) return;
    preorderModal.classList.add('active');
    preorderOverlay.classList.add('active');
    document.body.style.overflow = 'hidden';
  }

  /**
   * Close the modal
   */
  function closeModal() {
    if (!preorderModal || !preorderOverlay) return;
    preorderModal.classList.remove('active');
    preorderOverlay.classList.remove('active');
    document.body.style.overflow = '';
    
    // Reset transforms
    preorderModal.style.transform = '';
    preorderModal.style.opacity = '';
  }

  /**
   * Show loading state
   */
  function showLoading() {
    if (loadingEl) loadingEl.classList.add('active');
    if (optionsContainer) optionsContainer.innerHTML = '';
  }

  /**
   * Hide loading state
   */
  function hideLoading() {
    if (loadingEl) loadingEl.classList.remove('active');
  }

  /**
   * Open modal with a specific product
   * @param {string} productHandle - The product handle
   */
  function openWithProduct(productHandle) {
    // First, prepare the loading state (but don't open modal yet)
    if (optionsContainer) optionsContainer.innerHTML = '';
    
    // Fetch product data FIRST, then open modal after content is ready
    fetch('/products/' + productHandle + '.js')
      .then(function(response) { return response.json(); })
      .then(function(product) {
        // Populate options BEFORE opening modal
        populateOptions(product);
        
        // Now open modal with content already in place - smooth animation
        requestAnimationFrame(function() {
          openModal();
        });
      })
      .catch(function(error) {
        console.error('Error loading product:', error);
        if (optionsContainer) {
          optionsContainer.innerHTML = '<div class="preorder-qv-option"><span>Error loading options</span></div>';
        }
        openModal();
      });
  }

  /**
   * Populate the options list with variants
   * @param {Object} product - The product data
   */
  function populateOptions(product) {
    if (!optionsContainer) return;

    optionsContainer.innerHTML = '';
    const variants = product.variants;

    variants.forEach(function(variant) {
      const option = document.createElement('div');
      option.className = 'preorder-qv-option';
      option.dataset.variantId = variant.id;
      option.dataset.productHandle = product.handle;
      option.dataset.available = variant.available;

      // Determine initial status
      let status = '';
      let statusClass = '';

      if (!variant.available) {
        status = 'Sold Out';
        statusClass = 'is-sold-out';
      } else {
        // Show loading while we fetch metafields
        status = '';
        option.dataset.needsMetafields = 'true';
      }

      option.innerHTML = 
        '<span class="preorder-qv-option__title">' + variant.title + '</span>' +
        '<span class="preorder-qv-option__status">' + status + '</span>';

      if (statusClass) {
        option.classList.add(statusClass);
      }

      // Add click handler for immediate add to cart
      option.addEventListener('click', function() {
        handleVariantClick(this, variant.id, product.handle);
      });

      optionsContainer.appendChild(option);
    });

    // Fetch metafields for shipping dates
    fetchVariantMetafields(product.handle, variants);
  }

  /**
   * Fetch variant metafields for shipping dates
   * @param {string} handle - Product handle
   * @param {Array} variants - Array of variants
   */
  function fetchVariantMetafields(handle, variants) {
    variants.forEach(function(variant) {
      if (!variant.available) return;

      fetch('/products/' + handle + '?variant=' + variant.id + '&view=preorder-data')
        .then(function(response) { return response.text(); })
        .then(function(data) {
          try {
            const jsonData = JSON.parse(data);
            updateVariantStatus(variant.id, jsonData);
          } catch (e) {
            // If not JSON, show Waitlist
            updateVariantStatus(variant.id, { status: 'Waitlist' });
          }
        })
        .catch(function() {
          updateVariantStatus(variant.id, { status: 'Waitlist' });
        });
    });
  }

  /**
   * Update variant status display
   * @param {string} variantId - Variant ID
   * @param {Object} data - Metafield data
   */
  function updateVariantStatus(variantId, data) {
    const option = optionsContainer?.querySelector('[data-variant-id="' + variantId + '"]');
    if (!option) return;

    const statusEl = option.querySelector('.preorder-qv-option__status');
    if (!statusEl) return;

    let status = '';
    let statusClass = '';

    if (data.ship_by) {
      status = 'Shipping starts ' + data.ship_by;
    } else if (data.window_start && data.window_end) {
      status = 'Shipping ' + data.window_start + ' - ' + data.window_end;
    } else if (data.window_start) {
      status = 'Shipping starts ' + data.window_start;
    } else {
      status = 'Waitlist';
      statusClass = 'is-waitlist';
    }

    statusEl.textContent = status;
    if (statusClass) {
      option.classList.add(statusClass);
    }

    // Store shipping data as data attributes for use when adding to cart
    if (data.ship_by) {
      option.dataset.shipBy = data.ship_by;
      option.dataset.shipByRaw = data.ship_by_raw || '';
      option.dataset.isPreorder = 'true';
    } else if (data.window_start) {
      option.dataset.shippingWindowStart = data.window_start;
      option.dataset.shippingWindowEnd = data.window_end || '';
      option.dataset.isPreorder = 'true';
    }
  }

  /**
   * Handle variant click - immediately add to cart
   * @param {Element} optionEl - The clicked option element
   * @param {string} variantId - Variant ID
   * @param {string} handle - Product handle
   */
  function handleVariantClick(optionEl, variantId, handle) {
    // Don't do anything if sold out
    if (optionEl.classList.contains('is-sold-out')) return;

    // Show adding state
    optionEl.classList.add('is-adding');
    const statusEl = optionEl.querySelector('.preorder-qv-option__status');
    const originalStatus = statusEl ? statusEl.textContent : '';
    if (statusEl) statusEl.textContent = 'Adding to cart...';

    // Build preorder cart properties
    var cartProperties = {};
    var isPreorder = optionEl.dataset.isPreorder === 'true';
    var shipBy = optionEl.dataset.shipBy || '';
    var shipByRaw = optionEl.dataset.shipByRaw || '';
    var windowStart = optionEl.dataset.shippingWindowStart || '';
    var windowEnd = optionEl.dataset.shippingWindowEnd || '';
    var hasShippingDates = !!(shipBy || (windowStart && windowEnd));
    var isActivePreorder = isPreorder && hasShippingDates;
    cartProperties['_Status'] = isActivePreorder ? 'Pre-order' : 'In Stock';
    if (isActivePreorder) {
      if (shipByRaw) cartProperties['_Preorder_Date'] = shipByRaw;
      if (shipBy) cartProperties['Estimated shipping'] = shipBy;
      if (windowStart && windowEnd) cartProperties['Shipping window'] = 'from ' + windowStart + ' to ' + windowEnd;
    }

    // Add to cart
    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        id: variantId,
        quantity: 1,
        properties: cartProperties
      })
    })
    .then(function(response) { return response.json(); })
    .then(function(item) {
      closeModal();

      // Open mini cart
      const jsGridCart = document.querySelector('.js-mini-cart');
      if (jsGridCart) {
        setTimeout(function() {
          jsGridCart.classList.add('active');
        }, 300);
      }

      // Update cart elements if theme.miniCart exists
      if (typeof theme !== 'undefined' && theme.miniCart) {
        theme.miniCart.updateElements();
        theme.miniCart.generateCart();
      }
    })
    .catch(function(error) {
      console.error('Error adding to cart:', error);
      optionEl.classList.remove('is-adding');
      if (statusEl) statusEl.textContent = originalStatus;
      // Fallback: go to product page
      window.location.href = '/products/' + handle + '?variant=' + variantId;
    });
  }

  /**
   * Setup close button and overlay click handlers
   */
  function setupCloseHandlers() {
    if (closeBtn) {
      closeBtn.addEventListener('click', closeModal);
    }

    if (preorderOverlay) {
      preorderOverlay.addEventListener('click', closeModal);
    }
  }

  /**
   * Setup drag-to-dismiss functionality
   */
  function setupDragFunctionality() {
    if (!preorderModal) return;

    preorderModal.addEventListener('touchstart', function(e) {
      startY = e.touches[0].clientY;
      isDragging = true;
      preorderModal.style.transition = 'none';
    });

    preorderModal.addEventListener('touchmove', function(e) {
      if (!isDragging) return;

      currentY = e.touches[0].clientY;
      const deltaY = currentY - startY;

      // Only allow dragging downward
      if (deltaY > 0) {
        e.preventDefault();
        preorderModal.style.transform = 'translateY(' + deltaY + 'px)';

        const opacity = 1 - (deltaY / (2 * dragThreshold));
        if (preorderOverlay) {
          preorderOverlay.style.opacity = Math.max(0.3, opacity);
        }
      }
    });

    preorderModal.addEventListener('touchend', function() {
      if (!isDragging) return;
      isDragging = false;

      const deltaY = currentY - startY;
      preorderModal.style.transition = 'transform 0.3s ease, opacity 0.3s ease';

      if (deltaY > dragThreshold) {
        closeModal();
      } else {
        preorderModal.style.transform = 'translateY(0)';
        if (preorderOverlay) preorderOverlay.style.opacity = '';
      }

      startY = 0;
      currentY = 0;
    });
  }

  /**
   * Setup click handlers for preorder buy now buttons
   * Button is only visible on mobile, so just open the modal
   */
  function setupButtonClickHandlers() {
    document.addEventListener('click', function(e) {
      const btn = e.target.closest('.js-preorder-quickview-mobile');
      if (btn) {
        e.preventDefault();
        e.stopPropagation();

        // Check if button is disabled (sold out)
        if (btn.classList.contains('disabled')) return;

        const handle = btn.dataset.handle;
        
        // Open the preorder variant selector
        if (handle && window.preorderQuickviewMobile) {
          window.preorderQuickviewMobile.open(handle);
        }
      }
    });
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
